import useCodeBook from "../codebook/useCodeBook";
import { TransportOption } from "../../models/financing-request/transport-option/TransportOption";
import FoodOption from "../../models/financing-request/FoodOption";
import { TeachingAidOption } from "../../models/financing-request/teaching-aid-option/TeachingAidOption";
import { SchoolCarrier } from "../../models/financing-request/SchoolCarrier";
import SelectItem from "../../models/shared/SelectItem";
import { TransportOptionType } from "../../models/transport-option-type/TransportOptionType";
import SchoolCarrierTransportOption from "../../models/financing-request/transport-option/SchoolCarrierTransportOption";
import PersonalTransportOption from "../../models/financing-request/transport-option/PersonalTransportOption";
import PersonalPassengerTransportOption from "../../models/financing-request/transport-option/PersonalPassengerTransportOption";
import CompanionInPublicTransportOption from "../../models/financing-request/transport-option/CompanionInPublicTransportOption";
import AuthorizedCarrierTransportOption from "../../models/financing-request/transport-option/AuthorizedCarrierTransportOption";
import ElementarySchoolTeachingAidOption from "../../models/financing-request/teaching-aid-option/ElementarySchoolTeachingAidOption";
import HighSchoolTeachingAidOption from "../../models/financing-request/teaching-aid-option/HighSchoolTeachingAidOption";
import { useState, useEffect } from "react";
import FinancingRequestStudentDataInfo from "../../models/financing-request/FinancingRequestStudentDataInfo";
import FinancingRequestApiClient from "../../api/FinancingRequestApiClient";
import PaymentRequestApiClient from "../../api/PaymentRequestApiClient";
import { ProgramType } from "../../models/financing-request/ProgramType";
import { SpecialNeedType } from "../../models/special-need-type/SpecialNeedType";
import { AuthorizedCarrier } from "../../models/financing-request/AuthorizedCarrier";
import { numberFormat, currencyFormat, round, getCurrencyDisplayName, getSpecialNeedPrice } from "../../utilities/utilities";
import { CompanionInPublicTransportType } from "../../models/companion-transport-types/CompanionInPublicTransportType";
import Currency from "../../models/shared/Currency";
import TeachingAidOptionType from "../../models/shared/TeachingAidOptionType";
import PaymentRequestStudentData from "../../models/payment-request/PaymentRequestStudentData";
import PaymentRequestStudentDetails from "../../models/payment-request/PaymentRequestStudentDetails";
import AnnualAdministration from "../../models/admin/annual-administration/AnnualAdministration";
import FoodType from "../../models/shared/FoodType";

interface Props {
  financingRequestId?: number;
  paymentRequestId?: number;
  schoolYearId?: number;    
  month?: Date | null;
}

const useStudentDataInfo = ({
  financingRequestId = undefined,
  paymentRequestId = undefined,
  schoolYearId = undefined,  
  month = undefined,
}: Props) => {
  const [
    financingRequestStudentDataInfo,
    setFinancingRequestStudentDataInfo,
  ] = useState<FinancingRequestStudentDataInfo[]>(
    new Array<FinancingRequestStudentDataInfo>()
  );

  const [studentDataInfoReady, setStudentDataInfoReady] = useState<boolean>(
    false
  );

  const {
    transportOptionTypeSelectItems,
    vehicleTypeSelectItems,
    companionTransportTypeSelectItems,
  } = useCodeBook({
    getTransportOptionType: true,
    getVehicleType: true,
    getCompanionTransportType: true,
  });

  useEffect(() => {
    if (
      transportOptionTypeSelectItems &&
      vehicleTypeSelectItems &&
      transportOptionTypeSelectItems.length > 0 &&
      vehicleTypeSelectItems.length > 0
    ) {
      setStudentDataInfoReady(true);
    }
  }, [
    transportOptionTypeSelectItems,
    vehicleTypeSelectItems,
    financingRequestStudentDataInfo,
  ]);

  useEffect(() => {
    if (financingRequestId) {
      getDataInfo(financingRequestId);
    }
  }, [financingRequestId]);

  useEffect(() => {
    async function getDataInfo(id: number) {
      var api = new PaymentRequestApiClient();
      var dataInfo = await api.getFinancingRequestStudentDataInfoById(id);

      setFinancingRequestStudentDataInfo([...dataInfo]);
    }

    if (paymentRequestId) {
      getDataInfo(paymentRequestId);
    }
  }, [paymentRequestId]);

  useEffect(() => {
    async function getDataInfo(schoolYearId: number, month: Date) {
      var api = new PaymentRequestApiClient();
      var dataInfo = await api.getFinancingRequestStudentDataInfoByDate(
        schoolYearId,
        month
      );

      setFinancingRequestStudentDataInfo([...dataInfo]);
    }

    if (schoolYearId && month) {
      getDataInfo(schoolYearId, month);
    }
  }, [schoolYearId, month]);

  async function getDataInfo(id: number) {
    var api = new FinancingRequestApiClient();
    var dataInfo = await api.getFinancingRequestStudentDataInfo(id);

    setFinancingRequestStudentDataInfo([...dataInfo]);
  }

  function getTransportOptionInfoByStudentId(studentId: number, currency: Currency) {
    var info = financingRequestStudentDataInfo.find(
      (i) => i.studentId == studentId
    );

    if (info && info.transportOption) {
      return getTransportOptionDataInfo(
        info.transportOption,
        currency,
        info.schoolCarriers,
        info.authorizedCarriers        
      );
    }

    return "-";
  }

  function getFoodOptionInfoByStudentId(studentId: number) {
    var info = financingRequestStudentDataInfo.find(
      (i) => i.studentId == studentId
    );

    if (info && info.foodOption) {
      return getFoodOptionDataInfo(info.foodOption);
    }

    return "-";
  }

  async function getAnnualPricingInfo(schoolYearId: number) {
    var api = new PaymentRequestApiClient();
    const pricingRequest = await api.getAnualPricing();
  
    var pricing = pricingRequest.filter((price) => {
          return price.schoolYearId === schoolYearId;
        })[0];
    return pricing;      
  }

  function getTeachingAidOptionInfoByStudentId(
    studentId: number,
    programType: ProgramType,
    specialNeedType: SpecialNeedType,
    currency: Currency,
    annualPricing: AnnualAdministration|null
  ) {
    var info = financingRequestStudentDataInfo.find(
      (i) => i.studentId == studentId
    );

    
    if (info && info.teachingAidOption) {
      return getTeachingAidOptionDataInfo(
        info.teachingAidOption,
        programType,
        specialNeedType,
        currency,
        annualPricing
      );
    }

    return "-";
  }

  const getTransportOptionDataInfo = (
    transportOption: TransportOption,
    currency: Currency,
    schoolCarriers?: SchoolCarrier[],
    authorizedCarriers?: AuthorizedCarrier[]    
  ) => {
    if (transportOption && transportOption.transportOptionType) {
      let type = transportOptionTypeSelectItems.find(
        (tot) =>
          transportOption && tot.value == transportOption.transportOptionType
      );

      if (
        type == null &&
        transportOption.transportOptionType ==
          TransportOptionType.PersonalPassenger
      ) {
        const personal = transportOptionTypeSelectItems.find(
          (tot) => tot.value == TransportOptionType.Personal
        );
        if (personal) {
          const passenger: SelectItem<TransportOptionType> = {
            value: TransportOptionType.PersonalPassenger,
            text: personal.text,
          };
          type = passenger;
        }
      }

      if (type) {
        let info = "";
        if (type.value == TransportOptionType.SchoolCarrier) {
          const schoolCarrierTransportOption = transportOption as SchoolCarrierTransportOption;
          if (schoolCarrierTransportOption.vehicleType) {
            const vType = vehicleTypeSelectItems.find(
              (vti) => vti.value == schoolCarrierTransportOption.vehicleType
            );
            let schoolCarrier: SchoolCarrier | undefined = undefined;
            if (schoolCarriers) {
              schoolCarrier = schoolCarriers.find(
                (sc) =>
                  sc.vehicleType == schoolCarrierTransportOption.vehicleType &&
                  sc.registrationPlate ==
                    schoolCarrierTransportOption.registrationPlate
              );
            }

            info += vType
              ? ` (${vType.text}${
                  schoolCarrierTransportOption.registrationPlate
                    ? " " +
                      schoolCarrierTransportOption.registrationPlate +
                      (schoolCarrier
                        ? ", " +
                          numberFormat(
                            schoolCarrier.dailyMileageTotal || 0,
                            "km",
                            4
                          )
                        : "")
                    : ""
                })`
              : "";
          }
        } else if (type.value == TransportOptionType.Personal) {
          const personalTransportOption = transportOption as PersonalTransportOption;
          if (personalTransportOption.mileageFromHomeToSchool) {
            info +=
              ", " +
              numberFormat(
                personalTransportOption.mileageFromHomeToSchool || 0,
                "km",
                4
              ) +
              " (Ukupno: " +
              numberFormat(
                round(personalTransportOption.mileageFromHomeToSchool || 0, 4) *
                  4,
                "km",
                4
              ) +
              ")";
          }

          if (
            personalTransportOption.passengers &&
            personalTransportOption.passengers.length > 0
          ) {
            var passengersInfo = new Array<string>();
            personalTransportOption.passengers.forEach((passenger) => {
              passengersInfo.push(passenger.oib);
            });
            info += " (S učenikom se vozi: " + passengersInfo.join(", ") + ")";
          }
        } else if (type.value == TransportOptionType.PersonalPassenger) {
          if (transportOption) {
            const personalPassenger = transportOption as PersonalPassengerTransportOption;
            if (personalPassenger.personalStudent) {
              info =
                " (S učenikom: " + personalPassenger.personalStudent.oib + ")";
            }
          }
        } else if (
          type.value == TransportOptionType.CompanionInPublicTransport
        ) {
          const companion = transportOption as CompanionInPublicTransportOption;

          if (companion.companionInPublicTransportType) {
            let cardType = companionTransportTypeSelectItems.find(
              (c) => c.value == companion.companionInPublicTransportType
            );
            if (cardType) {
              info += ", " + cardType.text.toLowerCase();
            }
          }

          info += companion.ticketPrice
            ? " = " + currencyFormat(companion.ticketPrice || 0, currency)
            : "";
        } else if (type.value == TransportOptionType.AuthorizedCarrier) {
          const authorizedCarrierTransport = transportOption as AuthorizedCarrierTransportOption;
          let authorizedCarrier: AuthorizedCarrier | undefined = undefined;
          if (authorizedCarriers) {
            authorizedCarrier = authorizedCarriers.find(
              (ac) => ac.id == authorizedCarrierTransport.authorizedCarrierId
            );
          }

          if (authorizedCarrier) {
            info += " (";
            info += authorizedCarrier.name || "";
            info += authorizedCarrier.dailyMileage
              ? ", " + numberFormat(authorizedCarrier.dailyMileage, "km", 4)
              : "";
            info += authorizedCarrier.dailyPrice
              ? ", " + currencyFormat(authorizedCarrier.dailyPrice, currency) + " po danu"
              : "";
            info += ")";
          }
        }
        return type.text + info;
      }
    }
    return "-";
  };

  function getFoodOptionDataInfo(foodOption: FoodOption) {
    if (foodOption) {
      let info = new Array<string>();

      if (foodOption.breakfast && foodOption.lunch && foodOption.snack) {
        return "Tri obroka";
      } else if (foodOption.breakfast || foodOption.lunch || foodOption.snack) {
        if (foodOption.breakfast) info.push("Doručak");
        if (foodOption.lunch) info.push("Ručak");
        if (foodOption.snack) info.push("Užina");

        return info.join(", ");
      }
    }
    return "-";
  }

  function getTeachingAidOptionType(studentId: number,
    programType: ProgramType,
    specialNeedType: SpecialNeedType){
      var teachingAidOptionType = TeachingAidOptionType.Unknown;
      var info = financingRequestStudentDataInfo.find(
        (i) => i.studentId == studentId
      );

      //console.log({info: info, f:financingRequestStudentDataInfo});
      if (info && info.teachingAidOption) {
        const elementary = info.teachingAidOption as ElementarySchoolTeachingAidOption;
        const highSchool = info.teachingAidOption as HighSchoolTeachingAidOption;
  
        if (elementary.regular && programType == ProgramType.Elementary) {
          teachingAidOptionType = specialNeedType == SpecialNeedType.ExtendedProfessionalProcedure ||
          specialNeedType ==
          SpecialNeedType.SpecialCenterExtendedProfessionalProcedure
          ? TeachingAidOptionType.PSP1
          : TeachingAidOptionType.PSP2;          
        }
      }else{
        if (programType == ProgramType.Elementary) {
          teachingAidOptionType = specialNeedType == SpecialNeedType.ExtendedProfessionalProcedure ||
          specialNeedType ==
          SpecialNeedType.SpecialCenterExtendedProfessionalProcedure
          ? TeachingAidOptionType.PSP1
          : TeachingAidOptionType.PSP2;          
        }
      }
      return teachingAidOptionType;
    }

  function getTeachingAidOptionDataInfo(
    teachingAidOption: TeachingAidOption,
    programType: ProgramType,
    specialNeedType: SpecialNeedType,
    currency: Currency,
    annualPricing: AnnualAdministration|null
  ) {
    if (teachingAidOption) {
      const elementary = teachingAidOption as ElementarySchoolTeachingAidOption;
      const highSchool = teachingAidOption as HighSchoolTeachingAidOption;

if(annualPricing!== null && annualPricing === undefined)
{
  console.log("getTeachingAidOptionDataInfo - useStudentDataInfo - 381 " + schoolYearId);  
  console.log(annualPricing);
}

      if (elementary.regular && programType == ProgramType.Elementary) {
          return getSpecialNeedPrice(currency, specialNeedType, annualPricing);
      } else if (programType == ProgramType.High) {
        let info = new Array<string>();
        if (highSchool.regular) {
          info.push("Izvođenje nastave");
        }
        if (highSchool.practical) {
          info.push("Praktična nastava");
        }
        if (info != null && info.length > 0) {
          return info.join(", ");
        }
      }
    }
    return "-";
  }

  function getAmountsTorecalculateCostAfterFebruary2023(
    anualAdministration: AnnualAdministration,
    foodTypes: FoodType[],
    numberOfDays: number,
    foodCost: number,
    teachingAidCost: number,
    studentId: number,
    programType: ProgramType,
    specialNeedType: SpecialNeedType
  ){
    var recalculation = {
      food:0,
      teachinAid:0
    }

    var breakfeastPrice = anualAdministration.breakfeastPrice || 0;

    var hasBreakfast = false;
    var hasLunch = false;
    var psp50 = false;
    var psp75 = false;

    var foodCostToAdd = foodCost || 0;           
    var studentsBreakfastPrice = breakfeastPrice;
    if(foodCostToAdd > 0 ){
      if(foodTypes.filter(ft => ft == FoodType.Breakfast).length > 0)
      {
        studentsBreakfastPrice = (numberOfDays || 0) * breakfeastPrice
        hasBreakfast = true;
      }
      else{
        hasBreakfast = false;
      }
      if(foodTypes.filter(ft => ft == FoodType.Lunch).length > 0)
      {
        hasLunch = true;
      }
      else{
        hasLunch = false;
      }
    }

    var psp = getTeachingAidOptionType(studentId, programType, specialNeedType);

      switch(psp)
      {
        case TeachingAidOptionType.PSP1:
          psp50 = true;
          break;
        case TeachingAidOptionType.PSP2:
          psp75 = true;
          break;
        default:
        break;
      }

    if(hasBreakfast === true)
    {
      recalculation.food = studentsBreakfastPrice;
      if((teachingAidCost || 0) > 0 && (psp50 === true || psp75 === true)){
        recalculation.teachinAid = breakfeastPrice;
      }
    }


    return recalculation;
    
  }

  function getAmountsTorecalculateCostAfterFebruary2023WithoutBrekfast(
    breakfastPrice: number,
    foodTypes: FoodType[],
    numberOfDays: number,
    foodCost: number,
    teachingAidCost: number,
    studentId: number,
    programType: ProgramType,
    specialNeedType: SpecialNeedType
  ){
    var recalculation = {
      food:0,
      teachinAid:0
    }

    var hasBreakfast = false;
    var hasLunch = false;
    var psp50 = false;
    var psp75 = false;

    var foodCostToAdd = foodCost || 0;           
    var studentsBreakfastPrice = breakfastPrice;
    if(foodCostToAdd > 0 ){
      if(foodTypes.filter(ft => ft == FoodType.Breakfast).length > 0)
      {
        studentsBreakfastPrice = (numberOfDays || 0) * breakfastPrice
        hasBreakfast = true;
      }
      else{
        hasBreakfast = false;
      }
      if(foodTypes.filter(ft => ft == FoodType.Lunch).length > 0)
      {
        hasLunch = true;
      }
      else{
        hasLunch = false;
      }
    }

    var psp = getTeachingAidOptionType(studentId, programType, specialNeedType);
    switch(psp)
    {
      case TeachingAidOptionType.PSP1:
        psp50 = true;
        break;
      case TeachingAidOptionType.PSP2:
        psp75 = true;
        break;
      default:
      break;
    }
    
    if(hasBreakfast === true)
    {
      recalculation.food = studentsBreakfastPrice;
      if((psp50 === true || psp75 === true) && (teachingAidCost || 0) > 0 ){
        recalculation.teachinAid = breakfastPrice;
      }       
    }   

    return recalculation;
  }

  return {
    studentDataInfoReady,
    getTransportOptionDataInfo,
    getFoodOptionDataInfo,
    getTeachingAidOptionDataInfo,
    getTransportOptionInfoByStudentId,
    getFoodOptionInfoByStudentId,
    getTeachingAidOptionInfoByStudentId,
    getTeachingAidOptionType,
    getAmountsTorecalculateCostAfterFebruary2023,
    getAmountsTorecalculateCostAfterFebruary2023WithoutBrekfast,
    getAnnualPricingInfo
  };
};

export default useStudentDataInfo;
